import styled, {css, keyframes} from 'styled-components';

const cardEnter = keyframes`
  from { opacity: 0; transform: translate3d(0, 8px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

export const surfaceCard = css`
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadii.m}px;
  box-shadow: ${(p) => p.theme.elevation.sm};
`;

/**
 * Shared clickable card hover: lift, stronger shadow, violet border.
 * Keep this as the single source of truth for list / tile card interaction.
 */
export const interactiveCard = css`
  ${surfaceCard};
  position: relative;
  cursor: pointer;
  animation: ${cardEnter} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} backwards;
  transition: transform ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    box-shadow ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    border-color ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${(p) => p.theme.elevation.lg};
    border-color: ${(p) => p.theme.colors.violet300};
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;

    &:hover,
    &:active {
      transform: none;
    }
  }
`;

/** Tlo strony pod pelnoszerokimi panelami sekcji. */
export const pageMuted = css`
  background: ${(p) => p.theme.colors.surfaceMuted};
`;

/** Pelnoszeroki pasek sekcji: biale tlo, wloskowate obramowanie gora/dol. */
export const sectionPanel = css`
  width: 100%;
  background: ${(p) => p.theme.colors.surface};
  border-top: 1px solid ${(p) => p.theme.colors.border};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

export const SectionPanel = styled.section`
  ${sectionPanel};

  & + & {
    margin-top: 8px;
  }
`;

export const SectionPanelBody = styled.div`
  max-width: 1096px;
  width: 100%;
  margin: 0 auto;
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;

  @media screen and (max-width: 550px) {
    padding: 16px;
  }
`;

export const SectionPanelTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.primary500};
`;

export const chip = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${(p) => p.theme.colors.surfaceSunken};
  color: ${(p) => p.theme.colors.primary500};
`;

export const photoPlaceholderSurface = css`
  background: ${(p) => p.theme.colors.primary100};
  border: 1px dashed ${(p) => p.theme.colors.violet300};
  box-shadow: none;
  transition: background ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    border-color ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PhotoPlaceholderFrame = styled.div<{$size?: number}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size ?? 120}px;
  height: ${(p) => p.$size ?? 120}px;
  border-radius: 20px;
  overflow: hidden;
  flex-shrink: 0;
  ${photoPlaceholderSurface};
`;

export const PhotoPreviewFrame = styled.div<{$size?: number}>`
  position: relative;
  display: block;
  width: ${(p) => p.$size ?? 120}px;
  height: ${(p) => p.$size ?? 120}px;
  border-radius: 20px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${(p) => p.theme.colors.primary100};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const PhotoPlaceholderImageWrap = styled.div`
  display: inline-flex;
  border-radius: 20px;
  overflow: hidden;
  line-height: 0;
  ${photoPlaceholderSurface};

  img {
    display: block;
  }
`;

/**
 * Full-bleed muted page surface. The layout `main` has no background of its
 * own, so the tint is painted by a fixed layer sitting behind the content.
 */
export const MutedPageBackground = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  ${pageMuted};
`;

export const SectionLabel = styled.p`
  margin: 12px 16px 10px 20px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.textMuted};
`;
